<template>
  <div v-if="agrupacionesVisible.length > 0" class="agrupaciones-container">
    <div class="agrupaciones-grid">
      <div class="section-header">
        <h2>{{ $t(getPageData().titleAgrupaciones) }}</h2>
        <p>{{ $t(getPageData().subTitleAgrupaciones) }}</p>
      </div>
      <div
        v-for="(agrupacion, index) in agrupacionesVisible"
        :key="agrupacion.id"
        :class="['agrupacion-item', getClassByIndex(index)]"
        :style="getAgrupacionBackground(agrupacion.imagen)"
        class="pointer"
        @click="goToAgrupacion(agrupacion.uri)">
        <div class="agrupacion-content">
          <div class="agrupacion-header">
            <div class="agrupacion-title agrupacion-title-text">
              <div>{{ agrupacion.nombre }}</div>
            </div>
          </div>
          <div v-if="agrupacion.fechaInicio && agrupacion.fechaFin" class="agrupacion-date">
            <CalendaryIcon class="me-2" color="white" />
            <span>{{ normalizeFromToDates(agrupacion.fechaInicio, agrupacion.fechaFin) }}</span>
          </div>
        </div>
        <div class="agrupacion-location mb-3">
          <LocationIcon class="me-2" width="20px" color="white" />
          <span>{{ agrupacion.ubicacion }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import CalendaryIcon from '@/components/Icons/CalendaryIcon.vue';
  import LocationIcon from '@/components/Icons/LocationIcon.vue';
  import { normalizeFromToDates } from '@/helpers/dateUtiles';
  import { getPageData } from '@/helpers/domainUtils';
  import { AgrupacionDetalle } from '@/router/RouterNames';

  const props = defineProps({
    agrupaciones: {
      type: Array,
      required: true,
    },
  });

  const router = useRouter();
  const agrupacionesOrder = [
    'wintersnowfestival2025',
    'afterworkinthesnow2025',
    'andorra',
    'formigal',
    'alpesfranceses',
  ];

  const agrupacionesVisible = computed(() => {
    if (!props.agrupaciones) return [];

    const orderedAgrupaciones = props.agrupaciones.slice().sort((a, b) => {
      const indexA = agrupacionesOrder.indexOf(a.uri);
      const indexB = agrupacionesOrder.indexOf(b.uri);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      } else if (indexA !== -1) {
        return -1;
      } else if (indexB !== -1) {
        return 1;
      }
      return 0;
    });

    return orderedAgrupaciones.slice(0, 7);
  });

  function getAgrupacionBackground(image) {
    return {
      background: `linear-gradient(0deg, rgba(var(--bs-primary-rgb), 0.5) 0%, rgba(var(--bs-primary-rgb), 0.5) 100%), url(${image}) lightgray 50% / cover no-repeat`,
    };
  }

  function getClassByIndex(index) {
    const length = agrupacionesVisible.value.length;
    if (length === 1) return 'col-12';
    if (length === 3 || length === 6) return 'col-4';
    if (length === 7) {
      if (index < 2 || index >= 5) return 'col-6';
      return 'col-4';
    }
    if (length % 2 !== 0) {
      if (index < 2) return 'col-6';
      return 'col-4';
    }
    return 'col-6';
  }

  function goToAgrupacion(uri) {
    router.push({
      name: AgrupacionDetalle,
      params: { uri: uri },
    });
  }
</script>

<style scoped lang="scss">
  .section-header {
    text-align: left;
    margin-bottom: 19px;
    width: 100%;
  }

  .section-header h2 {
    font-size: 28px;
    font-style: italic;
    font-weight: 700;
    text-transform: uppercase;
    color: #ffffff;
  }

  .section-header p {
    font-size: 1.2rem;
    color: #ffffff;
    line-height: 1.5;
    margin: 0 auto;
  }

  .agrupaciones-container {
    width: 100%;
    justify-content: center;
    position: relative;
    z-index: 1;
    overflow: hidden;
  }

  .agrupaciones-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--bs-primary);
    clip-path: polygon(
      100.146% 0%,
      0% 0%,
      0% 17.505%,
      0% 37.631%,
      0% 75.996%,
      100% 100%,
      100% 37.631%,
      100.146% 37.631%,
      100.146% 0%
    );
    z-index: -1;
  }

  .agrupaciones-grid {
    display: flex;
    flex-wrap: wrap;
    padding: 50px 90px;
    z-index: 2;
  }

  .agrupacion-item {
    height: 400px;
    position: relative;
    overflow: hidden;
    color: white;
    display: flex;
    flex-direction: column;
    transition: transform 0.3s ease-in-out;
    justify-content: space-between;
    cursor: pointer; /* Añadido para mostrar que es clickeable */
  }

  .agrupacion-item:hover {
    transform: scale(1.05);
    z-index: 1001;
  }

  .agrupacion-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 20px;
    flex-grow: 1;
    text-align: center;
  }

  .agrupacion-header {
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
  }

  .agrupacion-icon img {
    max-width: 40%;
    max-height: 300px;
  }

  .agrupacion-title-text {
    color: #ffffff;
    text-align: center;
    text-shadow: 0px 0px 1px rgba(var(--bs-primary-rgb), 0.4), 2px 4px 5px rgba(var(--bs-primary-rgb), 0.1),
      4px 6px 20px rgba(var(--bs-primary-rgb), 0.1);
    font-size: 32px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .agrupacion-date {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    margin-top: 10px;
  }

  .agrupacion-location {
    display: flex;
    justify-content: center;
    align-items: center;
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    padding: 0 20px;
    padding-bottom: 10px;
    width: 100%;
  }

  .agrupacion-location span {
    margin-left: 4px;
  }

  @media (max-width: 1024px) {
    .agrupacion-item {
      width: 100% !important;
    }

    .agrupaciones-grid {
      display: flex;
      flex-wrap: wrap;
      padding: 30px 15px;
      z-index: 2;
    }
  }
</style>
